import { errorBodySchema } from '@crm/contracts';
import type {
  CustomFieldEntity,
  DueWindow,
  LayoutEntity,
  MyTasksFilter,
  RecordEntity,
  SavedViewEntity,
  TaskType,
} from '@crm/contracts';
import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from '../generated/openapi.d.ts';
import { getIdToken, tokenStore } from './auth.ts';
import { ApiError } from './api.ts';

/**
 * The typed API client (RELAY-937): `openapi-fetch` over `generated/openapi.d.ts`, which
 * `pnpm api:client` produces from the API's own OpenAPI document. A path, a query parameter
 * or a response field that does not exist on the API is a compile error here, and CI
 * regenerates the types and fails on a diff, so they cannot go stale.
 *
 * Every request carries the Cognito ID token; a 401 ends the session (the token store is
 * cleared and `crm:unauthenticated` is dispatched so the router can send the user to
 * sign-in). `call()` turns the `{ data, error }` result into a value-or-ApiError, the same
 * error the hand-written `apiFetch` throws, so the UI has one error vocabulary.
 */
export const UNAUTHENTICATED_EVENT = 'crm:unauthenticated';

const auth: Middleware = {
  async onRequest({ request }) {
    const token = await getIdToken();
    if (token) request.headers.set('authorization', `Bearer ${token}`);
    request.headers.set('accept', 'application/json');
    // Every change the app makes is a `ui` change in the stage history (RELAY-951).
    request.headers.set('x-change-source', 'ui');
    return request;
  },
  onResponse({ response }) {
    if (response.status === 401) {
      tokenStore.clear();
      window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
    }
    return response;
  },
};

// Absolute on purpose: `new Request('/api/…')` needs an origin outside a browser document (tests, workers).
export const client = createClient<paths>({
  baseUrl: `${window.location.origin}/api`,
  // Late-bound: the global fetch at CALL time (tests stub it per case; the dev mock wraps it at boot).
  fetch: (request) => fetch(request),
});
/**
 * Every successful write announces itself (RELAY-946), so record timelines refresh the moment a
 * change lands — whichever screen made it, and without each mutation having to remember to.
 * The session provider listens and invalidates the open timeline and history queries.
 */
export const WRITE_EVENT = 'crm:write';
const announceWrites: Middleware = {
  onResponse({ request, response }) {
    if (request.method !== 'GET' && response.ok) window.dispatchEvent(new Event(WRITE_EVENT));
    return response;
  },
};

client.use(auth);
client.use(announceWrites);

/** Unwrap an openapi-fetch result: the typed body, or an ApiError describing why not. */
export async function call<T>(
  request: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await request;
  } catch (err) {
    // openapi-fetch throws on a network failure and on a 2xx whose body is not JSON.
    const message = err instanceof Error ? err.message : 'Network error';
    throw err instanceof SyntaxError
      ? new ApiError(`Non-JSON response: ${message}`, 0, 'non_json_response')
      : new ApiError(message, 0, 'network_error');
  }
  if (result.response.ok && (result.data !== undefined || result.response.status === 204))
    return result.data as T; // a 204 has no body: the typed result is `undefined`

  const body = errorBodySchema.safeParse(result.error);
  if (body.success)
    throw new ApiError(
      body.data.error.message,
      result.response.status,
      body.data.error.code,
      body.data.request_id,
      body.data.error.details,
    );
  throw new ApiError(`HTTP ${result.response.status}`, result.response.status, 'http_error');
}

export type Me = paths['/me']['get']['responses'][200]['content']['application/json'];
export type FlagView =
  paths['/flags']['get']['responses'][200]['content']['application/json'][number];
export type MemberView =
  paths['/users']['get']['responses'][200]['content']['application/json'][number];
export type InviteBody = NonNullable<
  paths['/users']['post']['requestBody']
>['content']['application/json'];
export type RoleView =
  paths['/roles']['get']['responses'][200]['content']['application/json'][number];
export type TeamView =
  paths['/teams']['get']['responses'][200]['content']['application/json'][number];
export type ProjectRoleView =
  paths['/project-roles']['get']['responses'][200]['content']['application/json'][number];
export type RecordCountsView =
  paths['/stats/record-counts']['get']['responses'][200]['content']['application/json'];
export type TeamCreateBody = NonNullable<
  paths['/teams']['post']['requestBody']
>['content']['application/json'];
export type RoleCreateBody = NonNullable<
  paths['/roles']['post']['requestBody']
>['content']['application/json'];
export type RolePatchBody = NonNullable<
  paths['/roles/{key}']['patch']['requestBody']
>['content']['application/json'];
export type AuditPage = paths['/audit']['get']['responses'][200]['content']['application/json'];
export type AuditEntry = AuditPage['items'][number];
export type AuditQuery = NonNullable<paths['/audit']['get']['parameters']['query']>;
export type FieldDefinitionView =
  paths['/field-definitions']['get']['responses'][200]['content']['application/json'][number];
export type FieldDefinitionInput = NonNullable<
  paths['/field-definitions']['post']['requestBody']
>['content']['application/json'];
export type FieldDefinitionPatch = NonNullable<
  paths['/field-definitions/{id}']['patch']['requestBody']
>['content']['application/json'];
/** One candidate as its page reads it: the record plus its Candidate Flow places. */
export type CandidateDetailView =
  paths['/candidates/{id}']['get']['responses'][200]['content']['application/json'];
/** A candidate as lists and searches serve it (no pipeline places). */
export type CandidateRecord = Omit<CandidateDetailView, 'pipelines'>;
/** A record's place in one pipeline (a project's or a candidate's). */
export type PipelinePlaceView = CandidateDetailView['pipelines'][number];
export type CandidateInput = NonNullable<
  paths['/candidates']['post']['requestBody']
>['content']['application/json'];
export type CandidateSearchRequest = NonNullable<
  paths['/candidates:search']['post']['requestBody']
>['content']['application/json'];
export type SavedViewRecord =
  paths['/saved-views']['get']['responses'][200]['content']['application/json'][number];
export type SavedViewCreate = NonNullable<
  paths['/saved-views']['post']['requestBody']
>['content']['application/json'];
export type SavedViewPatchBody = NonNullable<
  paths['/saved-views/{id}']['patch']['requestBody']
>['content']['application/json'];
export type CandidateHistoryPage =
  paths['/candidates/{id}/history']['get']['responses'][200]['content']['application/json'];
export type CandidateHistoryEntry = CandidateHistoryPage['items'][number];
export type DocumentView =
  paths['/documents/{id}/download']['get']['responses'][200]['content']['application/json'];
export type CandidateDocument =
  paths['/candidates/{id}/documents']['get']['responses'][200]['content']['application/json'][number];
export type DocumentInitBody = NonNullable<
  paths['/candidates/{id}/documents:init']['post']['requestBody']
>['content']['application/json'];
export type DocumentCompleteBody = NonNullable<
  paths['/candidates/{id}/documents:complete']['post']['requestBody']
>['content']['application/json'];
export type TaskAttachmentInitBody = NonNullable<
  paths['/tasks/{id}/attachments:init']['post']['requestBody']
>['content']['application/json'];
export type TaskAttachment =
  paths['/tasks/{id}/attachments']['get']['responses'][200]['content']['application/json'][number];
export type DocumentRegistration = NonNullable<
  paths['/documents/']['post']['requestBody']
>['content']['application/json'];
export type OverviewLayoutView =
  paths['/layouts']['get']['responses'][200]['content']['application/json'];
export type LayoutPutBody = NonNullable<
  paths['/layouts']['put']['requestBody']
>['content']['application/json'];
export type NoteView =
  paths['/notes']['get']['responses'][200]['content']['application/json'][number];
export type NoteCreate = NonNullable<
  paths['/notes']['post']['requestBody']
>['content']['application/json'];
export type NotePatchBody = NonNullable<
  paths['/notes/{id}']['patch']['requestBody']
>['content']['application/json'];
export type NoteTemplateView =
  paths['/note-templates']['get']['responses'][200]['content']['application/json'][number];
export type NoteTemplateCreate = NonNullable<
  paths['/note-templates']['post']['requestBody']
>['content']['application/json'];
export type NoteTemplatePatchBody = NonNullable<
  paths['/note-templates/{id}']['patch']['requestBody']
>['content']['application/json'];
export type TaskView =
  paths['/tasks']['get']['responses'][200]['content']['application/json'][number];
export type TaskCreate = NonNullable<
  paths['/tasks']['post']['requestBody']
>['content']['application/json'];
export type TaskPatchBody = NonNullable<
  paths['/tasks/{id}']['patch']['requestBody']
>['content']['application/json'];
export type ActivityPage =
  paths['/candidates/{id}/activities']['get']['responses'][200]['content']['application/json'];
export type ActivityView = ActivityPage['items'][number];
export type CandidateBulkBody = NonNullable<
  paths['/candidates:bulkUpdate']['post']['requestBody']
>['content']['application/json'];
export type BulkResult =
  paths['/candidates:bulkUpdate']['post']['responses'][200]['content']['application/json'];
export type ExportBody = NonNullable<
  paths['/candidates:export']['post']['requestBody']
>['content']['application/json'];
export type DuplicateReport =
  paths['/candidates/duplicates']['get']['responses'][200]['content']['application/json'];
export type MergePreview =
  paths['/candidates/{id}/merge-preview']['get']['responses'][200]['content']['application/json'];
export type CandidatePage =
  paths['/candidates:search']['post']['responses'][200]['content']['application/json'];
export type PipelineView =
  paths['/pipelines']['get']['responses'][200]['content']['application/json'][number];
export type PipelineStageView = PipelineView['stages'][number];
export type StageCreateBody = NonNullable<
  paths['/pipelines/{id}/stages']['post']['requestBody']
>['content']['application/json'];
export type StagePatchBody = NonNullable<
  paths['/pipelines/{id}/stages/{stageId}']['patch']['requestBody']
>['content']['application/json'];
export type PipelineCreateBody = NonNullable<
  paths['/pipelines']['post']['requestBody']
>['content']['application/json'];
export type RollupRuleView =
  paths['/pipelines/rollup-rules']['get']['responses'][200]['content']['application/json'][number];
export type RollupRuleBody = NonNullable<
  paths['/pipelines/rollup-rules']['post']['requestBody']
>['content']['application/json'];
export type TriggerView =
  paths['/pipeline-triggers']['get']['responses'][200]['content']['application/json'][number];
export type TriggerWithSecretView =
  paths['/pipeline-triggers']['post']['responses'][201]['content']['application/json'];
export type TriggerBody = NonNullable<
  paths['/pipeline-triggers']['post']['requestBody']
>['content']['application/json'];

export type CompanyView =
  paths['/companies/{id}']['get']['responses'][200]['content']['application/json'];
/** A row of `POST /companies:search`: the company with its project's primary contact. */
export type CompanyListView =
  paths['/companies:search']['post']['responses'][200]['content']['application/json']['items'][number];
export type CompanyInput = NonNullable<
  paths['/companies']['post']['requestBody']
>['content']['application/json'];
export type CompanyPatchBody = NonNullable<
  paths['/companies/{id}']['patch']['requestBody']
>['content']['application/json'];
export type ContactView =
  paths['/contacts/{id}']['get']['responses'][200]['content']['application/json'];
export type ContactPatchBody = NonNullable<
  paths['/contacts/{id}']['patch']['requestBody']
>['content']['application/json'];
/** A record's Message Hub texts and emails (TS-1897/TS-1898). */
export type MessageRecordKind = 'projects' | 'contacts';
export type MessageConversationList =
  paths['/projects/{id}/messages/conversations']['get']['responses'][200]['content']['application/json'];
export type MessageConversation = MessageConversationList['items'][number];
/** Home's Pending messages and Calendar. */
export type PendingMessage =
  paths['/messages/pending']['get']['responses'][200]['content']['application/json']['items'][number];
export type MyMeetings =
  paths['/meetings/mine']['get']['responses'][200]['content']['application/json'];
export type MeetingView = MyMeetings['items'][number];
/** Team Tasks: the people chosen at the top of the page (empty = everyone the caller reaches). */
export interface TeamScope {
  teamIds: readonly string[];
  assigneeIds: readonly string[];
}
const teamQuery = (scope: TeamScope) => ({
  ...(scope.teamIds.length ? { teamIds: scope.teamIds.join(',') } : {}),
  ...(scope.assigneeIds.length ? { assigneeIds: scope.assigneeIds.join(',') } : {}),
});
export type TextMessagePage =
  paths['/projects/{id}/messages/texts']['get']['responses'][200]['content']['application/json'];
export type TextMessage = TextMessagePage['items'][number];
export type EmailMessagePage =
  paths['/projects/{id}/messages/emails']['get']['responses'][200]['content']['application/json'];
export type EmailMessage = EmailMessagePage['items'][number];
/** A project's Leve Payments and Fees & Billing, read from Leve. */
export type LeveProjectView =
  paths['/projects/{id}/leve']['get']['responses'][200]['content']['application/json'];
export type LeveOrganizationView = LeveProjectView['organizations'][number];
/** The KPI strip of a project's Leve organisations, for the project page's header. */
export type LeveKpisView =
  paths['/projects/{id}/leve/kpis']['get']['responses'][200]['content']['application/json'];
export type ProjectView =
  paths['/projects/{id}']['get']['responses'][200]['content']['application/json'];
export type ProjectInput = NonNullable<
  paths['/projects']['post']['requestBody']
>['content']['application/json'];
export type ProjectPatchBody = NonNullable<
  paths['/projects/{id}']['patch']['requestBody']
>['content']['application/json'];
export type ProjectContactView =
  paths['/projects/{id}/contacts']['get']['responses'][200]['content']['application/json'][number];
export type ProjectHistoryPage =
  paths['/projects/{id}/history']['get']['responses'][200]['content']['application/json'];
export type DealView = paths['/deals/{id}']['get']['responses'][200]['content']['application/json'];
export type DealInputBody = NonNullable<
  paths['/projects/{id}/deals']['post']['requestBody']
>['content']['application/json'];
export type JobView = paths['/jobs/{id}']['get']['responses'][200]['content']['application/json'];
export type BundleView =
  paths['/bundles/{id}']['get']['responses'][200]['content']['application/json'];
export type BundleInputBody = NonNullable<
  paths['/deals/{id}/bundles']['post']['requestBody']
>['content']['application/json'];
export type BundlePatchBody = NonNullable<
  paths['/bundles/{id}']['patch']['requestBody']
>['content']['application/json'];
export type JobInputBody = NonNullable<
  paths['/jobs']['post']['requestBody']
>['content']['application/json'];
export type JobPatchBody = NonNullable<
  paths['/jobs/{id}']['patch']['requestBody']
>['content']['application/json'];
export type JobSearchBody = NonNullable<
  paths['/jobs:search']['post']['requestBody']
>['content']['application/json'];
export type JobCountsBody = NonNullable<
  paths['/jobs:counts']['post']['requestBody']
>['content']['application/json'];
export type CompanySearchBody = NonNullable<
  paths['/companies:search']['post']['requestBody']
>['content']['application/json'];
export type ProjectBulkPipelineBody = NonNullable<
  paths['/projects:add-to-pipeline']['post']['requestBody']
>['content']['application/json'];
export type ProjectBulkPipelineResult =
  paths['/projects:add-to-pipeline']['post']['responses'][200]['content']['application/json'];
export type PlaceBulkAddBody = NonNullable<
  paths['/deals:add-to-pipeline']['post']['requestBody']
>['content']['application/json'];
export type ProjectSearchBody = NonNullable<
  paths['/projects:search']['post']['requestBody']
>['content']['application/json'];
export type ProjectCountsBody = NonNullable<
  paths['/projects:counts']['post']['requestBody']
>['content']['application/json'];
export type DealSearchBody = NonNullable<
  paths['/deals:search']['post']['requestBody']
>['content']['application/json'];
export type DealCountsBody = NonNullable<
  paths['/deals:counts']['post']['requestBody']
>['content']['application/json'];
export type ShortlistMemberSearchBody = NonNullable<
  paths['/shortlists/{id}/candidates:search']['post']['requestBody']
>['content']['application/json'];
export type ShortlistMemberCountsBody = NonNullable<
  paths['/shortlists/{id}/candidates:counts']['post']['requestBody']
>['content']['application/json'];
export type ShortlistMemberView =
  paths['/shortlists/{id}/candidates']['get']['responses'][200]['content']['application/json'][number];
export type StageCountView =
  paths['/jobs:counts']['post']['responses'][200]['content']['application/json'][number];
export type WebhookView =
  paths['/webhooks']['get']['responses'][200]['content']['application/json'][number];
export type WebhookWithSecretView =
  paths['/webhooks']['post']['responses'][201]['content']['application/json'];
export type WebhookInput = NonNullable<
  paths['/webhooks']['post']['requestBody']
>['content']['application/json'];
export type WebhookPatch = NonNullable<
  paths['/webhooks/{id}']['patch']['requestBody']
>['content']['application/json'];
/** Settings → System Health (TS-1039). */
export type SystemHealthView =
  paths['/admin/system-health']['get']['responses'][200]['content']['application/json'];
export type BreakerView = SystemHealthView['breakers'][number];
export type LogLevelView = SystemHealthView['logLevels'][number];
export type DeadLetterView =
  paths['/admin/jobs/dead-letter']['get']['responses'][200]['content']['application/json'][number];
export type ServiceName = LogLevelView['service'];
/** Performance on System Health (TS-1070). */
export type PerformanceView =
  paths['/admin/performance']['get']['responses'][200]['content']['application/json'];
export type RuntimeLogLevel = NonNullable<LogLevelView['override']>;
export type WebhookDeliveryView =
  paths['/webhooks/{id}/deliveries']['get']['responses'][200]['content']['application/json']['items'][number];
export type ApiKeyView =
  paths['/api-keys']['get']['responses'][200]['content']['application/json'][number];
export type ApiKeyWithSecretView =
  paths['/api-keys']['post']['responses'][201]['content']['application/json'];
export type ApiKeyCreateBody = NonNullable<
  paths['/api-keys']['post']['requestBody']
>['content']['application/json'];
export type ApiKeyPatchBody = NonNullable<
  paths['/api-keys/{id}']['patch']['requestBody']
>['content']['application/json'];
export type CandidateJobView =
  paths['/candidates/{id}/jobs']['get']['responses'][200]['content']['application/json'][number];
export type CandidateStageHistoryEntry =
  paths['/shortlists/{id}/candidates/{cid}/stage-history']['get']['responses'][200]['content']['application/json'][number];
export type ShortlistView =
  paths['/jobs/{id}/shortlists']['get']['responses'][200]['content']['application/json'][number];

/** Team Tasks' filters (TS-1743) other than the type; `assigneeIds` empty = everyone in scope. */
export interface TeamTasksFilters {
  teamIds: readonly string[];
  assigneeIds: readonly string[];
  status: MyTasksFilter;
  due?: DueWindow | undefined;
  entityType?: RecordEntity | undefined;
  automated?: boolean | undefined;
}

const teamTasksQuery = (q: TeamTasksFilters) => ({
  status: q.status,
  ...(q.teamIds.length > 0 ? { teamIds: q.teamIds.join(',') } : {}),
  ...(q.assigneeIds.length > 0 ? { assigneeIds: q.assigneeIds.join(',') } : {}),
  ...(q.due ? { due: q.due } : {}),
  ...(q.entityType ? { entityType: q.entityType } : {}),
  ...(q.automated !== undefined
    ? { automated: q.automated ? ('true' as const) : ('false' as const) }
    : {}),
});

export const api = {
  companies: {
    list: (q?: string, cursor?: string) =>
      call(
        client.GET('/companies', {
          params: { query: { limit: 50, ...(q ? { q } : {}), ...(cursor ? { cursor } : {}) } },
        }),
      ),
    search: (body: CompanySearchBody) => call(client.POST('/companies:search', { body })),
    get: (id: string) => call(client.GET('/companies/{id}', { params: { path: { id } } })),
    create: (body: CompanyInput) => call(client.POST('/companies', { body })),
    patch: (id: string, body: CompanyPatchBody) =>
      call(client.PATCH('/companies/{id}', { params: { path: { id } }, body })),
  },
  contacts: {
    list: (q?: string) =>
      call(client.GET('/contacts', { params: { query: { limit: 50, ...(q ? { q } : {}) } } })),
    create: (body: { firstName?: string; lastName?: string; email?: string; companyId?: string }) =>
      call(client.POST('/contacts', { body })),
    get: (id: string) => call(client.GET('/contacts/{id}', { params: { path: { id } } })),
    patch: (id: string, body: ContactPatchBody) =>
      call(client.PATCH('/contacts/{id}', { params: { path: { id } }, body })),
  },
  projects: {
    list: (opts: { q?: string; cursor?: string; limit?: number } = {}) =>
      call(
        client.GET('/projects', {
          params: {
            query: {
              limit: opts.limit ?? 50,
              ...(opts.q ? { q: opts.q } : {}),
              ...(opts.cursor ? { cursor: opts.cursor } : {}),
            },
          },
        }),
      ),
    get: (id: string) => call(client.GET('/projects/{id}', { params: { path: { id } } })),
    /** The projects a person is on, by their email (the Meeting Companion's attendees). */
    lookup: (email: string) => call(client.POST('/projects:lookup', { body: { email } })),
    create: (body: ProjectInput) => call(client.POST('/projects', { body })),
    patch: (id: string, body: ProjectPatchBody) =>
      call(client.PATCH('/projects/{id}', { params: { path: { id } }, body })),
    /** Put a person in one of the project's functions (its id or slug), or clear it with null. */
    setResponsible: (id: string, role: string, userId: string | null) =>
      call(
        client.PUT('/projects/{id}/responsibles/{role}', {
          params: { path: { id, role } },
          body: { userId },
        }),
      ),
    /** Put the project in another pipeline at a stage (its first open one when omitted, TS-1747). */
    addToPipeline: (id: string, body: { pipelineId: string; stageId?: string }) =>
      call(client.POST('/projects/{id}/pipelines', { params: { path: { id } }, body })),
    /** Put many projects in a pipeline; ones already in it (or not the caller's) are skipped. */
    addManyToPipeline: (body: ProjectBulkPipelineBody) =>
      call(client.POST('/projects:add-to-pipeline', { body })),
    /** Fold away its place in a pipeline it won or lost in; the API refuses an open one. */
    archiveInPipeline: (id: string, pipelineId: string) =>
      call(
        client.POST('/projects/{id}/pipelines/{pipelineId}:archive', {
          params: { path: { id, pipelineId } },
        }),
      ),
    restoreInPipeline: (id: string, pipelineId: string) =>
      call(
        client.POST('/projects/{id}/pipelines/{pipelineId}:restore', {
          params: { path: { id, pipelineId } },
        }),
      ),
    history: (id: string, cursor?: string) =>
      call(
        client.GET('/projects/{id}/history', {
          params: { path: { id }, query: { limit: 50, ...(cursor ? { cursor } : {}) } },
        }),
      ),
    contacts: (id: string) =>
      call(client.GET('/projects/{id}/contacts', { params: { path: { id } } })),
    addContact: (
      id: string,
      body: { contactId: string; role?: string | null; isPrimary?: boolean },
    ) =>
      call(
        client.POST('/projects/{id}/contacts', {
          params: { path: { id } },
          body: { isPrimary: false, ...body },
        }),
      ),
    patchContact: (
      id: string,
      contactId: string,
      body: { role?: string | null; isPrimary?: boolean },
    ) =>
      call(
        client.PATCH('/projects/{id}/contacts/{contactId}', {
          params: { path: { id, contactId } },
          body,
        }),
      ),
    removeContact: (id: string, contactId: string) =>
      call(
        client.DELETE('/projects/{id}/contacts/{contactId}', {
          params: { path: { id, contactId } },
        }),
      ),
    search: (body: ProjectSearchBody) => call(client.POST('/projects:search', { body })),
    counts: (body: ProjectCountsBody) => call(client.POST('/projects:counts', { body })),
    deals: (id: string) => call(client.GET('/projects/{id}/deals', { params: { path: { id } } })),
    addDeal: (id: string, body: DealInputBody) =>
      call(client.POST('/projects/{id}/deals', { params: { path: { id } }, body })),
  },
  deals: {
    get: (id: string) => call(client.GET('/deals/{id}', { params: { path: { id } } })),
    search: (body: DealSearchBody) => call(client.POST('/deals:search', { body })),
    counts: (body: DealCountsBody) => call(client.POST('/deals:counts', { body })),
    /** A stage of the default moves the deal's status; one of an extra pipeline, its place there. */
    moveStatus: (id: string, stageId: string) =>
      call(client.POST('/deals/{id}:move-status', { params: { path: { id } }, body: { stageId } })),
    /** Put many deals in an extra Deal Flow pipeline; ones already in it are skipped. */
    addManyToPipeline: (body: PlaceBulkAddBody) =>
      call(client.POST('/deals:add-to-pipeline', { body })),
  },
  /** Bundles under a deal (TS-925 follow-up): hires bought together, a slot per hire. */
  bundles: {
    ofDeal: (dealId: string) =>
      call(client.GET('/deals/{id}/bundles', { params: { path: { id: dealId } } })),
    /** Every bundle of the client, whichever deal bought it: what its jobs may use. */
    ofProject: (projectId: string) =>
      call(client.GET('/projects/{id}/bundles', { params: { path: { id: projectId } } })),
    create: (dealId: string, body: BundleInputBody) =>
      call(client.POST('/deals/{id}/bundles', { params: { path: { id: dealId } }, body })),
    patch: (id: string, body: BundlePatchBody) =>
      call(client.PATCH('/bundles/{id}', { params: { path: { id } }, body })),
    remove: (id: string) => call(client.DELETE('/bundles/{id}', { params: { path: { id } } })),
  },
  jobs: {
    get: (id: string) => call(client.GET('/jobs/{id}', { params: { path: { id } } })),
    ofProject: (projectId: string) =>
      call(client.GET('/jobs', { params: { query: { projectId, limit: 200 } } })),
    create: (body: JobInputBody) => call(client.POST('/jobs', { body })),
    patch: (id: string, body: JobPatchBody) =>
      call(client.PATCH('/jobs/{id}', { params: { path: { id } }, body })),
    /** A stage of the default moves the Job Status; one of an extra pipeline, its place there. */
    moveStatus: (id: string, stageId: string) =>
      call(client.POST('/jobs/{id}:move-status', { params: { path: { id } }, body: { stageId } })),
    /** Put many jobs in an extra Job Flow pipeline; ones already in it are skipped. */
    addManyToPipeline: (body: PlaceBulkAddBody) =>
      call(client.POST('/jobs:add-to-pipeline', { body })),
    /** With `includeArchived`, also the candidates taken off each shortlist (`archivedAt` set). */
    shortlists: (id: string, opts: { includeArchived?: boolean } = {}) =>
      call(
        client.GET('/jobs/{id}/shortlists', {
          params: {
            path: { id },
            ...(opts.includeArchived ? { query: { includeArchived: 'true' as const } } : {}),
          },
        }),
      ),
    search: (body: JobSearchBody) => call(client.POST('/jobs:search', { body })),
    counts: (body: JobCountsBody) => call(client.POST('/jobs:counts', { body })),
    ofCandidate: (candidateId: string) =>
      call(client.GET('/candidates/{id}/jobs', { params: { path: { id: candidateId } } })),
    /** One page of a shortlist's candidates (a shortlist board column, RELAY-1038). */
    shortlistMembers: (shortlistId: string, body: ShortlistMemberSearchBody) =>
      call(
        client.POST('/shortlists/{id}/candidates:search', {
          params: { path: { id: shortlistId } },
          body,
        }),
      ),
    shortlistCounts: (shortlistId: string, body: ShortlistMemberCountsBody) =>
      call(
        client.POST('/shortlists/{id}/candidates:counts', {
          params: { path: { id: shortlistId } },
          body,
        }),
      ),
    /** Shortlist management (TS-1599): another batch, rename / status, delete an empty one. */
    addShortlist: (jobId: string, name: string) =>
      call(
        client.POST('/jobs/{id}/shortlists', { params: { path: { id: jobId } }, body: { name } }),
      ),
    patchShortlist: (
      id: string,
      body: { name?: string; status?: 'draft' | 'presented' | 'closed' },
    ) => call(client.PATCH('/shortlists/{id}', { params: { path: { id } }, body })),
    deleteShortlist: (id: string) =>
      call(client.DELETE('/shortlists/{id}', { params: { path: { id } } })),
    /** Put a candidate on a shortlist (first open Shortlist Flow stage unless `stageId`). */
    addMember: (
      shortlistId: string,
      body: { candidateId: string; stageId?: string; visibleToClient?: boolean },
    ) =>
      call(
        client.POST('/shortlists/{id}/candidates', {
          params: { path: { id: shortlistId } },
          body: { visibleToClient: false, ...body },
        }),
      ),
    removeMember: (shortlistId: string, candidateId: string) =>
      call(
        client.DELETE('/shortlists/{id}/candidates/{cid}', {
          params: { path: { id: shortlistId, cid: candidateId } },
        }),
      ),
    /** Take a candidate off a shortlist (the place is archived, not deleted) or put them back. */
    archiveMember: (shortlistId: string, candidateId: string) =>
      call(
        client.POST('/shortlists/{id}/candidates/{cid}:archive', {
          params: { path: { id: shortlistId, cid: candidateId } },
        }),
      ),
    restoreMember: (shortlistId: string, candidateId: string) =>
      call(
        client.POST('/shortlists/{id}/candidates/{cid}:restore', {
          params: { path: { id: shortlistId, cid: candidateId } },
        }),
      ),
    setMemberVisibility: (shortlistId: string, candidateId: string, visibleToClient: boolean) =>
      call(
        client.PATCH('/shortlists/{id}/candidates/{cid}', {
          params: { path: { id: shortlistId, cid: candidateId } },
          body: { visibleToClient },
        }),
      ),
    /**
     * The same PATCH the shortlist table uses: history, audit and activity on the candidate. Into
     * a won stage, `replacement` says whether the hire takes one of the job's openings (TS-925).
     */
    moveMember: (
      shortlistId: string,
      candidateId: string,
      stageId: string,
      replacement?: boolean,
    ) =>
      call(
        client.PATCH('/shortlists/{id}/candidates/{cid}', {
          params: { path: { id: shortlistId, cid: candidateId } },
          body: { stageId, ...(replacement !== undefined ? { replacement } : {}) },
        }),
      ),
    /** A hire's kind, in place: a replacement takes none of the job's openings (TS-925). */
    setMemberReplacement: (shortlistId: string, candidateId: string, replacement: boolean) =>
      call(
        client.PATCH('/shortlists/{id}/candidates/{cid}', {
          params: { path: { id: shortlistId, cid: candidateId } },
          body: { replacement },
        }),
      ),
    stageHistory: (shortlistId: string, candidateId: string) =>
      call(
        client.GET('/shortlists/{id}/candidates/{cid}/stage-history', {
          params: { path: { id: shortlistId, cid: candidateId } },
        }),
      ),
  },
  /** Settings → Webhooks (TS-958). The signing secret is in the create and rotate responses only. */
  webhooks: {
    list: () => call(client.GET('/webhooks')),
    create: (body: WebhookInput) => call(client.POST('/webhooks', { body })),
    patch: (id: string, body: WebhookPatch) =>
      call(client.PATCH('/webhooks/{id}', { params: { path: { id } }, body })),
    rotate: (id: string, graceMinutes = 60) =>
      call(
        client.POST('/webhooks/{id}:rotate-secret', {
          params: { path: { id } },
          body: { graceMinutes },
        }),
      ),
    remove: (id: string) => call(client.DELETE('/webhooks/{id}', { params: { path: { id } } })),
    test: (id: string) => call(client.POST('/webhooks/{id}:test', { params: { path: { id } } })),
    deliveries: (
      id: string,
      query: { status?: WebhookDeliveryView['status']; cursor?: string; limit?: number } = {},
    ) =>
      call(
        client.GET('/webhooks/{id}/deliveries', {
          params: { path: { id }, query: { limit: 50, ...query } },
        }),
      ),
    replay: (deliveryId: string) =>
      call(
        client.POST('/webhooks/deliveries/{id}:replay', { params: { path: { id: deliveryId } } }),
      ),
  },
  /** Settings → System Health (TS-1039): breakers, queues, dead letters, jobs, sync, log levels. */
  /** A project's Leve Payments and Fees & Billing, read from Leve, behind leve.enabled. */
  leve: {
    project: (id: string) => call(client.GET('/projects/{id}/leve', { params: { path: { id } } })),
    kpis: (id: string) =>
      call(client.GET('/projects/{id}/leve/kpis', { params: { path: { id } } })),
  },
  /** A project's or a contact's Message Hub texts and emails (TS-1897), behind messages.enabled. */
  messages: {
    /** Home's Pending messages: conversations on my projects where the client spoke last. */
    pending: () => call(client.GET('/messages/pending')),
    conversations: (kind: MessageRecordKind, id: string) =>
      kind === 'projects'
        ? call(client.GET('/projects/{id}/messages/conversations', { params: { path: { id } } }))
        : call(client.GET('/contacts/{id}/messages/conversations', { params: { path: { id } } })),
    texts: (kind: MessageRecordKind, id: string, conversation: string, cursor?: string) => {
      const query = { conversation, limit: 50, ...(cursor ? { cursor } : {}) };
      return kind === 'projects'
        ? call(client.GET('/projects/{id}/messages/texts', { params: { path: { id }, query } }))
        : call(client.GET('/contacts/{id}/messages/texts', { params: { path: { id }, query } }));
    },
    emails: (kind: MessageRecordKind, id: string, cursor?: string) => {
      const query = { limit: 50, ...(cursor ? { cursor } : {}) };
      return kind === 'projects'
        ? call(client.GET('/projects/{id}/messages/emails', { params: { path: { id }, query } }))
        : call(client.GET('/contacts/{id}/messages/emails', { params: { path: { id }, query } }));
    },
    /** Reply to a text conversation through Twilio (TS-1904), behind messages.replies. */
    reply: (kind: MessageRecordKind, id: string, conversation: string, body: string) => {
      const opts = { params: { path: { id } }, body: { conversation, body } };
      return kind === 'projects'
        ? call(client.POST('/projects/{id}/messages/texts', opts))
        : call(client.POST('/contacts/{id}/messages/texts', opts));
    },
  },
  system: {
    health: () => call(client.GET('/admin/system-health')),
    performance: () => call(client.GET('/admin/performance')),
    deadLetters: () => call(client.GET('/admin/jobs/dead-letter')),
    retry: (id: string) =>
      call(client.POST('/admin/jobs/dead-letter/{id}:retry', { params: { path: { id } } })),
    discard: (id: string) =>
      call(client.DELETE('/admin/jobs/dead-letter/{id}', { params: { path: { id } } })),
    /** The message feed's set-aside rows (TS-1892): review, then dismiss. */
    feedRejections: (feed: 'texts' | 'emails') =>
      call(client.GET('/admin/message-feed/rejections', { params: { query: { feed } } })),
    dismissFeedRejections: (feed: 'texts' | 'emails') =>
      call(client.POST('/admin/message-feed/rejections:dismiss', { body: { feed } })),
    setLogLevel: (service: ServiceName, level: RuntimeLogLevel | null) =>
      call(
        client.PUT('/admin/log-levels/{service}', {
          params: { path: { service } },
          body: { level },
        }),
      ),
  },
  /** Settings → API keys (TS-954). A secret is in the create and rotate responses only. */
  apiKeys: {
    list: () => call(client.GET('/api-keys')),
    create: (body: ApiKeyCreateBody) => call(client.POST('/api-keys', { body })),
    patch: (id: string, body: ApiKeyPatchBody) =>
      call(client.PATCH('/api-keys/{id}', { params: { path: { id } }, body })),
    rotate: (id: string, graceMinutes: number) =>
      call(
        client.POST('/api-keys/{id}:rotate', { params: { path: { id } }, body: { graceMinutes } }),
      ),
    revoke: (id: string) =>
      call(client.POST('/api-keys/{id}:revoke', { params: { path: { id } } })),
  },
  /** Ingested emails and messages nothing matched yet (TS-955), and retrying them. */
  ingest: {
    unmatched: (limit = 20) =>
      call(client.GET('/activities/unmatched', { params: { query: { limit } } })),
    rematch: () => call(client.POST('/activities/unmatched:rematch')),
  },
  me: () => call(client.GET('/me')),
  switchOrganisation: (organisationId: string) =>
    call(client.POST('/me/organisation', { body: { organisationId } })),
  flags: () => call(client.GET('/flags')),
  setFlag: (name: FlagView['name'], enabled: boolean, note?: string) =>
    call(
      client.PUT('/flags/{name}', {
        params: { path: { name } },
        body: { enabled, ...(note === undefined ? {} : { note }) },
      }),
    ),
  users: () => call(client.GET('/users')),
  /** Membership changes (TS-956): invite, activate, deactivate, change role. */
  members: {
    invite: (body: InviteBody) => call(client.POST('/users', { body })),
    activate: (id: string) =>
      call(client.POST('/users/{id}/activate', { params: { path: { id } } })),
    deactivate: (id: string) =>
      call(client.POST('/users/{id}/deactivate', { params: { path: { id } } })),
    setRole: (id: string, role: string) =>
      call(client.PUT('/users/{id}/role', { params: { path: { id } }, body: { role } })),
    reassign: (id: string, toUserId: string) =>
      call(client.POST('/users/{id}/reassign', { params: { path: { id } }, body: { toUserId } })),
  },
  roles: {
    list: () => call(client.GET('/roles')),
    create: (body: RoleCreateBody) => call(client.POST('/roles', { body })),
    patch: (key: string, body: RolePatchBody) =>
      call(client.PATCH('/roles/{key}', { params: { path: { key } }, body })),
    remove: (key: string) => call(client.DELETE('/roles/{key}', { params: { path: { key } } })),
  },
  /** Project functions (Settings → Projects): who answers for a project in which capacity. */
  projectRoles: {
    list: () => call(client.GET('/project-roles')),
    create: (name: string) => call(client.POST('/project-roles', { body: { name } })),
    rename: (id: string, name: string) =>
      call(client.PATCH('/project-roles/{id}', { params: { path: { id } }, body: { name } })),
    reorder: (ids: string[]) => call(client.PUT('/project-roles/order', { body: { ids } })),
    remove: (id: string) =>
      call(client.DELETE('/project-roles/{id}', { params: { path: { id } } })),
  },
  /** How many live records of each kind the organisation holds. */
  stats: {
    recordCounts: () => call(client.GET('/stats/record-counts')),
  },
  /** Teams and who leads them (TS-1861). Every write answers with the whole team. */
  teams: {
    list: () => call(client.GET('/teams')),
    create: (body: TeamCreateBody) => call(client.POST('/teams', { body })),
    rename: (id: string, name: string) =>
      call(client.PATCH('/teams/{id}', { params: { path: { id } }, body: { name } })),
    remove: (id: string) => call(client.DELETE('/teams/{id}', { params: { path: { id } } })),
    addMember: (id: string, userId: string, isLead = false) =>
      call(
        client.POST('/teams/{id}/members', { params: { path: { id } }, body: { userId, isLead } }),
      ),
    setLead: (id: string, userId: string, isLead: boolean) =>
      call(
        client.PATCH('/teams/{id}/members/{userId}', {
          params: { path: { id, userId } },
          body: { isLead },
        }),
      ),
    removeMember: (id: string, userId: string) =>
      call(client.DELETE('/teams/{id}/members/{userId}', { params: { path: { id, userId } } })),
  },
  audit: (query: AuditQuery) => call(client.GET('/audit', { params: { query } })),
  fieldDefinitions: {
    list: (entity: CustomFieldEntity = 'candidates') =>
      call(client.GET('/field-definitions', { params: { query: { entity } } })),
    create: (body: FieldDefinitionInput) => call(client.POST('/field-definitions', { body })),
    patch: (id: string, body: FieldDefinitionPatch) =>
      call(client.PATCH('/field-definitions/{id}', { params: { path: { id } }, body })),
    remove: (id: string) =>
      call(client.DELETE('/field-definitions/{id}', { params: { path: { id } } })),
  },
  documents: {
    /** Register a document; the caller then PUTs the bytes to `uploadUrl`. */
    register: (body: DocumentRegistration) => call(client.POST('/documents/', { body })),
    downloadUrl: (id: string, disposition: 'inline' | 'attachment' = 'inline') =>
      call(
        client.GET('/documents/{id}/download', {
          params: { path: { id }, query: { disposition } },
        }),
      ),
  },
  layouts: {
    get: (entity: LayoutEntity = 'candidates') =>
      call(client.GET('/layouts', { params: { query: { entity } } })),
    put: (body: LayoutPutBody) => call(client.PUT('/layouts', { body })),
  },
  activities: (
    entityId: string,
    opts: { types?: readonly string[]; cursor?: string } = {},
    entityType: RecordEntity = 'candidates',
  ) =>
    call(
      // Every record kind serves the same timeline shape at `/{entityType}/{id}/activities`
      // (RELAY-946); the candidates path stands in for the type, which is identical for all.
      client.GET(`/${entityType}/{id}/activities` as '/candidates/{id}/activities', {
        params: {
          path: { id: entityId },
          query: {
            limit: 50,
            ...(opts.types?.length ? { types: opts.types.join(',') } : {}),
            ...(opts.cursor ? { cursor: opts.cursor } : {}),
          },
        },
      }),
    ),
  notes: {
    /** `includeRelated` adds the notes of every record `sources` lists (a project's). */
    list: (entityType: RecordEntity, entityId: string, opts: { includeRelated?: boolean } = {}) =>
      call(
        client.GET('/notes', {
          params: {
            query: {
              entityType,
              entityId,
              ...(opts.includeRelated ? { includeRelated: 'true' } : {}),
            },
          },
        }),
      ),
    /** The records whose notes the record's Notes card lists, with labels and write access. */
    sources: (entityType: RecordEntity, entityId: string) =>
      call(client.GET('/notes/sources', { params: { query: { entityType, entityId } } })),
    create: (body: NoteCreate) => call(client.POST('/notes', { body })),
    patch: (id: string, body: NotePatchBody) =>
      call(client.PATCH('/notes/{id}', { params: { path: { id } }, body })),
    remove: (id: string) => call(client.DELETE('/notes/{id}', { params: { path: { id } } })),
  },
  /** Note templates: `entityType` keeps the global ones and those of that kind. */
  noteTemplates: {
    list: (entityType?: RecordEntity) =>
      call(
        client.GET('/note-templates', {
          params: { query: entityType ? { entityType } : {} },
        }),
      ),
    create: (body: NoteTemplateCreate) => call(client.POST('/note-templates', { body })),
    patch: (id: string, body: NoteTemplatePatchBody) =>
      call(client.PATCH('/note-templates/{id}', { params: { path: { id } }, body })),
    remove: (id: string) =>
      call(client.DELETE('/note-templates/{id}', { params: { path: { id } } })),
  },
  /** Home's Calendar: my Calendly meetings overlapping from–to (my day, in my time zone). */
  meetings: {
    mine: (from: string, to: string) =>
      call(client.GET('/meetings/mine', { params: { query: { from, to } } })),
    /** Team Tasks' Calendar: the chosen people's meetings, each with its host. */
    team: (from: string, to: string, scope: TeamScope) =>
      call(client.GET('/meetings/team', { params: { query: { from, to, ...teamQuery(scope) } } })),
  },
  tasks: {
    list: (entityType: RecordEntity, entityId: string) =>
      call(client.GET('/tasks', { params: { query: { entityType, entityId } } })),
    /** `automated` false = manual only, true = automated only, undefined = both. */
    mine: (status: MyTasksFilter = 'open', type?: TaskType, automated?: boolean) =>
      call(
        client.GET('/tasks/mine', {
          params: {
            query: {
              status,
              ...(type ? { type } : {}),
              ...(automated !== undefined ? { automated: automated ? 'true' : 'false' } : {}),
            },
          },
        }),
      ),
    /** How many of My Tasks each type would list under the other filters. */
    mineTypeCounts: (status: MyTasksFilter, automated?: boolean) =>
      call(
        client.GET('/tasks/mine/type-counts', {
          params: {
            query: {
              status,
              ...(automated !== undefined ? { automated: automated ? 'true' : 'false' } : {}),
            },
          },
        }),
      ),
    /** Team Tasks (TS-1743): `assigneeIds` empty = everyone the caller's scope reaches. */
    team: (q: TeamTasksFilters & { type?: TaskType | undefined }) =>
      call(
        client.GET('/tasks/team', {
          params: { query: { ...teamTasksQuery(q), ...(q.type ? { type: q.type } : {}) } },
        }),
      ),
    /** How many Team Tasks each type would list under the other filters. */
    teamTypeCounts: (q: TeamTasksFilters) =>
      call(client.GET('/tasks/team/type-counts', { params: { query: teamTasksQuery(q) } })),
    create: (body: TaskCreate) => call(client.POST('/tasks', { body })),
    patch: (id: string, body: TaskPatchBody) =>
      call(client.PATCH('/tasks/{id}', { params: { path: { id } }, body })),
    remove: (id: string) => call(client.DELETE('/tasks/{id}', { params: { path: { id } } })),
    /** An automated task's run: what it sent and the answer. */
    automation: (id: string) =>
      call(client.GET('/tasks/{id}/automation', { params: { path: { id } } })),
    /** A task's files (TS-1741), newest first. */
    attachments: (id: string) =>
      call(client.GET('/tasks/{id}/attachments', { params: { path: { id } } })),
    attachmentsInit: (id: string, body: TaskAttachmentInitBody) =>
      call(client.POST('/tasks/{id}/attachments:init', { params: { path: { id } }, body })),
    attachmentsComplete: (id: string, body: DocumentCompleteBody) =>
      call(client.POST('/tasks/{id}/attachments:complete', { params: { path: { id } }, body })),
    removeAttachment: (id: string, documentId: string) =>
      call(
        client.DELETE('/tasks/{id}/attachments/{documentId}', {
          params: { path: { id, documentId } },
        }),
      ),
  },
  pipelines: {
    /** Live pipelines; `includeArchived` adds archived ones (Settings → Pipelines, TS-1746). */
    list: (opts: { includeArchived?: boolean } = {}) =>
      call(
        client.GET('/pipelines', {
          params: { query: opts.includeArchived ? { includeArchived: 'true' } : {} },
        }),
      ),
    create: (body: PipelineCreateBody) => call(client.POST('/pipelines', { body })),
    /** How many records sit in each stage, every pipeline at once; empty stages are left out. */
    stageCounts: () => call(client.GET('/pipelines/stage-counts')),
    archive: (id: string) =>
      call(client.POST('/pipelines/{id}:archive', { params: { path: { id } } })),
    unarchive: (id: string) =>
      call(client.POST('/pipelines/{id}:unarchive', { params: { path: { id } } })),
    rename: (id: string, name: string) =>
      call(client.PATCH('/pipelines/{id}', { params: { path: { id } }, body: { name } })),
    addStage: (id: string, body: StageCreateBody) =>
      call(client.POST('/pipelines/{id}/stages', { params: { path: { id } }, body })),
    patchStage: (id: string, stageId: string, body: StagePatchBody) =>
      call(
        client.PATCH('/pipelines/{id}/stages/{stageId}', {
          params: { path: { id, stageId } },
          body,
        }),
      ),
    removeStage: (id: string, stageId: string) =>
      call(
        client.DELETE('/pipelines/{id}/stages/{stageId}', { params: { path: { id, stageId } } }),
      ),
    reorder: (id: string, stageIds: string[]) =>
      call(
        client.PUT('/pipelines/{id}/stages/order', {
          params: { path: { id } },
          body: { stageIds },
        }),
      ),
    rollupRules: {
      list: () => call(client.GET('/pipelines/rollup-rules')),
      create: (body: RollupRuleBody) => call(client.POST('/pipelines/rollup-rules', { body })),
      remove: (ruleId: string) =>
        call(client.DELETE('/pipelines/rollup-rules/{ruleId}', { params: { path: { ruleId } } })),
    },
  },
  /** Pipeline triggers (flag `pipelines.triggers`): a stage's automations. */
  triggers: {
    list: (pipelineId: string) =>
      call(client.GET('/pipeline-triggers', { params: { query: { pipelineId } } })),
    create: (body: TriggerBody) => call(client.POST('/pipeline-triggers', { body })),
    replace: (id: string, body: TriggerBody) =>
      call(client.PUT('/pipeline-triggers/{id}', { params: { path: { id } }, body })),
    setEnabled: (id: string, enabled: boolean) =>
      call(
        client.PATCH('/pipeline-triggers/{id}', { params: { path: { id } }, body: { enabled } }),
      ),
    remove: (id: string) =>
      call(client.DELETE('/pipeline-triggers/{id}', { params: { path: { id } } })),
    runExisting: (id: string, dryRun: boolean) =>
      call(
        client.POST('/pipeline-triggers/{id}:run-existing', {
          params: { path: { id } },
          body: { dryRun },
        }),
      ),
    /** A new signing secret; the old one keeps signing for the default hour. */
    rotateSecret: (id: string) =>
      call(
        client.POST('/pipeline-triggers/{id}:rotate-secret', {
          params: { path: { id } },
          body: {},
        }),
      ),
  },
  dedupeRules: {
    list: () => call(client.GET('/dedupe-rules')),
    set: (field: 'email' | 'phone', mode: 'off' | 'warn' | 'block') =>
      call(client.PUT('/dedupe-rules', { body: { entity: 'candidates', field, mode } })),
  },
  savedViews: {
    list: (entity: SavedViewEntity = 'candidates') =>
      call(client.GET('/saved-views', { params: { query: { entity } } })),
    create: (body: SavedViewCreate) => call(client.POST('/saved-views', { body })),
    patch: (id: string, body: SavedViewPatchBody) =>
      call(client.PATCH('/saved-views/{id}', { params: { path: { id } }, body })),
    remove: (id: string) => call(client.DELETE('/saved-views/{id}', { params: { path: { id } } })),
  },
  candidates: {
    search: (body: CandidateSearchRequest) => call(client.POST('/candidates:search', { body })),
    get: (id: string) => call(client.GET('/candidates/{id}', { params: { path: { id } } })),
    /** `allowDuplicate`: the person saw a duplicate warning and chose to continue (RELAY-947). */
    create: (body: CandidateInput, opts: { allowDuplicate?: boolean } = {}) =>
      call(
        client.POST('/candidates', {
          body,
          params: { query: opts.allowDuplicate ? { allowDuplicate: 'true' } : {} },
        }),
      ),
    patch: (id: string, body: CandidateInput, opts: { allowDuplicate?: boolean } = {}) =>
      call(
        client.PATCH('/candidates/{id}', {
          params: { path: { id }, query: opts.allowDuplicate ? { allowDuplicate: 'true' } : {} },
          body,
        }),
      ),
    /** Put the candidate in a Candidate Flow pipeline at a stage (its first open one when omitted). */
    addToPipeline: (id: string, body: { pipelineId: string; stageId?: string }) =>
      call(client.POST('/candidates/{id}/pipelines', { params: { path: { id } }, body })),
    /** Fold away its place in a pipeline it won or lost in; the API refuses an open one. */
    archiveInPipeline: (id: string, pipelineId: string) =>
      call(
        client.POST('/candidates/{id}/pipelines/{pipelineId}:archive', {
          params: { path: { id, pipelineId } },
        }),
      ),
    restoreInPipeline: (id: string, pipelineId: string) =>
      call(
        client.POST('/candidates/{id}/pipelines/{pipelineId}:restore', {
          params: { path: { id, pipelineId } },
        }),
      ),
    duplicates: () => call(client.GET('/candidates/duplicates')),
    mergePreview: (id: string, otherId: string) =>
      call(
        client.GET('/candidates/{id}/merge-preview', {
          params: { path: { id }, query: { otherId } },
        }),
      ),
    merge: (id: string, otherId: string, takeFromOther: string[]) =>
      call(
        client.POST('/candidates/{id}/merge', {
          params: { path: { id } },
          body: { otherId, takeFromOther },
        }),
      ),
    remove: (id: string) => call(client.DELETE('/candidates/{id}', { params: { path: { id } } })),
    documents: (id: string) =>
      call(client.GET('/candidates/{id}/documents', { params: { path: { id } } })),
    documentsInit: (id: string, body: DocumentInitBody) =>
      call(client.POST('/candidates/{id}/documents:init', { params: { path: { id } }, body })),
    documentsComplete: (id: string, body: DocumentCompleteBody) =>
      call(client.POST('/candidates/{id}/documents:complete', { params: { path: { id } }, body })),
    history: (id: string, cursor?: string) =>
      call(
        client.GET('/candidates/{id}/history', {
          params: { path: { id }, query: { limit: 50, ...(cursor ? { cursor } : {}) } },
        }),
      ),
    /** The CSV as text; the caller turns it into a download. */
    bulkUpdate: (body: CandidateBulkBody) => call(client.POST('/candidates:bulkUpdate', { body })),
    exportCsv: async (body: ExportBody): Promise<string> => {
      const res = await client.POST('/candidates:export', { body, parseAs: 'text' });
      if (!res.response.ok) {
        const parsed = errorBodySchema.safeParse(res.error);
        throw new ApiError(
          parsed.success ? parsed.data.error.message : `HTTP ${res.response.status}`,
          res.response.status,
          parsed.success ? parsed.data.error.code : 'http_error',
        );
      }
      return res.data ?? '';
    },
    /** The file behind the candidate's RecruitCRM resume link, read by the API (TS-1896). */
    resumeLinkBytes: async (id: string): Promise<ArrayBuffer> => {
      const res = await client.GET('/candidates/{id}/resume-link', {
        params: { path: { id } },
        parseAs: 'arrayBuffer',
      });
      if (!res.response.ok) {
        const parsed = errorBodySchema.safeParse(res.error);
        throw new ApiError(
          parsed.success ? parsed.data.error.message : `HTTP ${res.response.status}`,
          res.response.status,
          parsed.success ? parsed.data.error.code : 'http_error',
        );
      }
      return res.data as ArrayBuffer;
    },
  },
};
